import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlarmClock, ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchOverdueTickets, type AdminTicket } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A7.14 — Kechikkan tiketlar. GET /admin/tickets, ochiq/kutilayotgan + yosh > SLA
 * (72s, client-side). Eng eskisi tepada bo'lishi uchun yosh ustuni bo'yicha saralanadi.
 */
export function TicketsOverdue() {
  const t = useT()
  const navigate = useNavigate()

  const columns = useMemo<AdminColumn<AdminTicket>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      {
        key: 'user_id',
        header: t.support.user,
        type: 'text',
        width: 130,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      { key: 'subject', header: t.support.subject, type: 'text', minWidth: 200 },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'open', label: t.support.open, status: 'open' },
          { value: 'pending', label: t.support.pending, status: 'pending' },
        ],
      },
      {
        key: 'created_at',
        header: t.support.age,
        type: 'number',
        width: 110,
        align: 'right',
        render: (r) => {
          const days = Math.floor((Date.now() - r.created_at) / 86_400_000)
          return <span className="block text-right font-mono tabular text-danger">{t.support.days(days)}</span>
        },
      },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchOverdueTickets(params), [])

  return (
    <>
      <PageHeader
        title={t.support.overdueTitle}
        screenId="A7.14"
        actions={
          <Link
            to="/support"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.support.queue}
          </Link>
        }
      />
      <DataTable<AdminTicket>
        screenId="A7.14"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        onRowClick={(r) => navigate(`/support/${r.id}`)}
        emptyState={{ icon: <AlarmClock size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
